import {
  BarChart3,
  FerrisWheel,
  Gamepad2,
  Globe,
  Headphones,
  Landmark,
  Megaphone,
  Mountain,
  QrCode,
  RefreshCw,
  Rocket,
  Ship,
  Smartphone,
  Tent,
  Ticket,
  Users,
  WalletCards,
  Waves,
  Wrench,
  Zap
} from 'lucide-react';

export const data = {
  heroData: {
    heading: <>Upgrade Your <br className="hidden lg:block" /><span className="">Ticketing With Greet</span></>,
    subHeading: "Greet helps waterparks, amusement parks, gaming zones, and attractions replace outdated ticketing systems with a faster, more reliable platform built for modern visitor operations.",
    badgeText: "Now onboarding attractions",
    features: [
      { text: 'Launch Your Platform in 24 Hours', icon: Zap },
      { text: 'Seamless Migration & Setup Support', icon: Rocket },
      { text: 'Complete Online & Offline Ticketing', icon: Ticket },
      { text: 'Real-Time Sales & Analytics App', icon: BarChart3 },
      { text: '24/7 Marketing & Growth Support Included', icon: Megaphone },
      { text: 'Lifetime Tech & Operational Support', icon: Headphones },
    ],
    form: {
      title: "What challenges are you facing currently?",
      textAreaPlaceholder: "Slow support, outdated ticketing system, entry issues, poor analytics, payment delays, or operational challenges."
    }
  },
  problemData: {
    heading: "Outgrowing Your Current Ticketing System?",
    subHeading: "As attractions grow, outdated ticketing systems often create operational bottlenecks, slower visitor experiences, reporting limitations, and dependency on unresponsive vendors. Greet helps attractions upgrade to a faster, more scalable platform without disrupting operations.",
    painPoints: [
      'Slow or unreliable ticketing systems',
      'Poor support during operational issues',
      'Long visitor queues during peak hours',
      'Limited analytics and reporting visibility',
      'Multiple disconnected systems',
      'Expensive maintenance or hidden charges',
      'Difficulty scaling during high visitor traffic',
      'Slow response for operational or pricing changes',
    ],
    footerText: "Greet removes all these barriers - so you can focus on running your attraction, not fixing broken systems."
  },
  featureData: {
    heading: "Why Attractions Upgrade to Greet",
    subHeading: "Outdated systems slow down operations, visitor experiences, and growth. Greet helps attractions switch to a faster, more reliable platform built for modern ticketing and visitor management.",
    features: [
      { id: 1, name: 'Faster & More Reliable Operations', desc: 'Built for smooth visitor management during weekends, holidays, and high-footfall days.', icon: Zap },
      { id: 2, name: 'Seamless Migration Support', desc: 'Switch from your current platform with dedicated onboarding and migration assistance.', icon: RefreshCw },
      { id: 3, name: 'Modern Website & Booking Experience', desc: 'Upgrade your attraction’s online presence with a faster and more visitor-friendly booking experience.', icon: Globe },
      { id: 4, name: 'Smarter Online & Offline Ticketing', desc: 'Manage online bookings, walk-ins, counter sales, and group bookings through one platform.', icon: Ticket },
      { id: 5, name: 'Fast QR-Based Visitor Entry', desc: 'Reduce queues and improve operational flow with faster QR-based visitor check-ins.', icon: QrCode },
      { id: 6, name: 'Real-Time Sales & Analytics', desc: 'Track visitor trends, bookings, operational performance, and revenue through a live dashboard.', icon: BarChart3 },
      { id: 7, name: 'Powerful Dashboard & Mobile App', desc: 'Manage your attraction operations anytime through one centralized platform.', icon: Smartphone },
      { id: 8, name: 'Lifetime Tech & Operational Support', desc: 'From pricing updates to operational assistance, our support team stays connected long-term.', icon: Headphones },
      { id: 9, name: 'Built for High-Footfall Attractions', desc: 'Designed specifically for waterparks, amusement parks, gaming zones, and attractions.', icon: Rocket },
    ]
  },
  industriesData: {
    heading: "Who We Help Upgrade",
    subHeading: "From entertainment venues to seasonal parks, Greet powers exceptional guest experiences.",
    industries: [
      { title: 'Waterparks', description: 'Manage online bookings, QR-based visitor entry, and high-footfall operations smoothly through one centralized platform.', icon: Waves },
      { title: 'Amusement Parks', description: 'Handle ticket sales, crowd management, and visitor operations efficiently during weekends, holidays, and seasonal rush.', icon: FerrisWheel },
      { title: 'Gaming Zones & Arcades', description: 'Simplify bookings, walk-ins, timed entries, and customer management for indoor entertainment and gaming experiences.', icon: Gamepad2 },
      { title: 'Adventure Parks', description: 'Manage adventure activities, visitor entry, ticketing, and operational flow seamlessly through one powerful system.', icon: Mountain },
      { title: 'Museums & Attractions', description: 'Enable organized visitor management, digital bookings, and smoother guest experiences for exhibitions and attractions.', icon: Landmark },
      {         title: 'Adventure Trips & Tour Packages',
        description: 'Manage bookings, itineraries, group experiences, and traveler operations seamlessly.', icon: Tent },
      { title: 'Family Entertainment Centers', description: 'Manage kids play zones, indoor attractions, and entertainment centers with streamlined bookings and visitor management.', icon: Users },
      { title: 'Water Sports & Activities', description: 'Simplify activity bookings, visitor coordination, and ticketing for boating, water sports, and adventure experiences.', icon: Ship },
    ]
  },
  primaryCtaData: {
    heading: "Ready to experience the difference?",
    buttonText: "Let's Talk, We're Way Better"
  },
  metricsData: {
    metrics: [
      { value: '10M+', label: 'Transactions' },
      { value: '1,00,000+', label: 'Tickets Sold' },
      { value: '5,000+', label: 'Successful Events Managed' },
      { value: '35+', label: 'Cities & Counting', desc: 'Trusted by organizers across India.' },
      { value: '50+', label: 'Live Attractions', desc: 'Waterparks, museums, parks, etc.' },
      { value: '4.8★', label: 'Satisfaction Score', desc: 'Backed by real organizers and institutions.' },
    ]
  },
  testimonialsData: {
    heading: "What Attraction Owners Say About Greet",
    subHeading: "Real experiences from waterparks, amusement parks, and attractions using Greet to manage ticketing, operations, and visitor experiences.",
    testimonials: [
      { id: 1, title: 'Launching our online booking system was much easier than expected.', description: 'Greet helped us set up ticketing, visitor entry, and our website within days. The team handled everything smoothly, and the support has been excellent throughout.', author: 'Operations Manager', role: 'Exotica Club', rating: 5 },
      { id: 2, title: 'The QR entry system made weekends much smoother.', description: 'Earlier, we struggled with long queues during peak hours. After switching to Greet, visitor entry became much faster and easier to manage.', author: 'Owner', role: 'Entertainment Kingdom', rating: 5 },
      { id: 3, title: "We didn't need to hire any developers.", description: 'The biggest advantage for us was that Greet already had a ready platform built for attractions. We launched quickly without spending lakhs on software development.', author: 'Founder', role: 'Akhela Water Sports', rating: 5 },
      { id: 4, title: 'Their support team actually stays connected.', description: 'Most companies disappear after onboarding. With Greet, their operations and tech team still helps us with pricing updates, website changes, and day-to-day support whenever needed.', author: 'Manager', role: 'SSB Kingdom', rating: 5 },
      { id: 5, title: 'The analytics dashboard helps us track everything live.', description: "We can now monitor ticket sales, visitor flow, and peak hours directly from the app. It's helped us make much better operational decisions.", author: 'Manager', role: 'Tourist Attraction & Park', rating: 5 },
      { id: 6, title: 'Greet also helped us improve our online visibility.', description: 'Apart from ticketing, their team guided us with promotions and digital marketing support which helped increase our bookings during weekends and holidays.', author: 'Marketing Head', role: 'SSB Gamezone', rating: 5 },
      { id: 7, title: 'Managing online and offline ticket sales became simple.', description: 'Before Greet, we used separate systems for counter sales and online bookings. Now everything is managed from one platform, which saves our team a lot of time.', author: 'Operations Team', role: 'Theme Park', rating: 5 },
      { id: 8, title: 'The platform handled heavy visitor traffic smoothly.', description: 'During festivals and holiday rush, the system remained fast and stable. That reliability is very important for attractions like ours.', author: 'Owner', role: 'Seasonal Attraction & Waterpark', rating: 5 },
    ]
  },
  comparisonData: {
    heading: "Why Attractions Upgrade to Greet",
    subHeading: "From smoother operations to better support and scalability, Greet helps attractions move beyond outdated ticketing systems confidently.",
    comparisons: [
      { feature: 'Platform Performance', icon: Zap, greet: 'Fast & reliable during peak traffic', competitor: 'Lag and operational slowdowns' },
      { feature: 'Ticketing System', icon: Ticket, greet: 'Unified online & offline ticketing', competitor: 'Fragmented or outdated systems' },
      { feature: 'Analytics & Insights', icon: BarChart3, greet: 'Real-time dashboard & mobile insights', competitor: 'Limited reporting visibility' },
      { feature: 'Website Experience', icon: Globe, greet: 'Modern booking & website experience', competitor: 'Often outdated or separate vendors' },
      { feature: 'Support', icon: Headphones, greet: 'Lifetime tech & operational support', competitor: 'Delayed or ticket-based support' },
      { feature: 'Marketing Support', icon: Megaphone, greet: 'Included marketing & growth support', competitor: 'Rarely available' },
      { feature: 'Scalability', icon: Rocket, greet: 'Built for high-footfall attractions', competitor: 'Operational limitations during rush' },
      { feature: 'Operational Flexibility', icon: Wrench, greet: 'Quick pricing & setup updates', competitor: 'Slow vendor dependency' },
      { feature: 'Pricing Structure', icon: WalletCards, greet: 'No expensive redevelopment costs', competitor: 'High maintenance & upgrade fees' },
    ]
  },
  secondaryCtaData: {
    heading: "Ready to Upgrade Your Attraction Operations?",
    subHeading: "Replace outdated ticketing systems with a faster, more reliable platform built for modern attractions and high visitor traffic.",
    buttonText: "Talk to Our Team"
  },
  eventsData: {
    events: [
      {
        id: 1,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1744638553397_1706606350134_Get%252520High%252520on%252520Entertainment%25252010%252520by%25252020%252520feet%252520copy%252520%2525281%252529.webp&w=1080&q=75",
        name: "Entertainment Kingdom Sambalpur",
        eventAddress: "Silat, Odisha",
        link: "https://greet.fydo.in/entertainment-kingdom-sambalpur",
      },
      {
        id: 2,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1746426864119_SSB%2520waterpark_25x10%2520%25282%2529%2520%25281%2529.jpg&w=1080&q=75",
        name: "SSB Kingdom Water Park",
        eventAddress: "Ghodabara, Odisha",
        link: "https://greet.fydo.in/ssb-kingdom-water-park",
      },
      {
        id: 3,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1749827612515_Exotica%2520Aloha%2521%252025X10.jpg&w=1080&q=75",
        name: "TheExoticaAIohaClub",
        eventAddress: "Cuttack, Odisha",
        link: "https://greet.fydo.in/theexoticaalohaclub",
      },
      {
        id: 4,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1746248345794_SSB%2520Game%2520zone_25x10%2520%25281%2529%2520%25281%2529.jpg&w=1080&q=75",
        name: "SSB Game Zone",
        eventAddress: "Cuttack, Odisha",
        link: "https://greet.fydo.in/ssb-game-zone",
      },
      {
        id: 5,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1745409204479_banner-45x20-v1-aprl-22.jpg&w=1080&q=75",
        name: "Blue Splash Water Park Puri",
        eventAddress: "Puri, Odisha",
        link: "https://greet.fydo.in/blue-splash-water-park",
      },
      {
        id: 6,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1738946798438_WhatsApp%2520Image%25202025-02-07%2520at%25207.47.52%2520PM.jpeg&w=1080&q=75",
        name: "Akhela Water Sports",
        eventAddress: "Sambalpur, Odisha",
        link: "https://greet.fydo.in/akhela-water-sports",
      },
    ],
  }
};
